/**
 * The throw as a **billiard that closes on itself**. And as a **spin that
 * unwinds itself**. Ported from Armarium (`apps/web/src/lib/dice3d/animate.ts`).
 *
 * The path is a billiard inside a rectangle: the die shoots off its home slot,
 * ricochets off the walls of the container, and ends up exactly where it
 * started.
 *
 * That it arrives is **arithmetic, not correction**. A billiard in a rectangle
 * is a straight line if you unfold the table at every wall instead of
 * mirroring the ball (`fold` folds it back up). On that straight line, "back
 * home" is simply a distance that can be computed for a given number of wall
 * hits. `spanFor` computes it. Because the distance is right, the homecoming
 * holds **at any speed**: the speed lives entirely in `tau`, and `tau(1) = 1`
 * always. Nothing pulls the die home, nothing snaps. The path never led
 * anywhere else.
 *
 * **And the spin works the same way.** The pose at time `s` is written down
 * (`poseAt`), not integrated: it is the target pose, rotated *backwards* about
 * two axes by `-(1-s)^spinEase` times several whole turns. At `s = 1` the
 * back-rotation is zero. The target pose is not the destination of a motion,
 * it is the **origin** of the rotation. So there is no final slerp that visibly
 * swings the die onto its number.
 *
 * Physics stays where you can see it: the height is real ballistics (gravity,
 * impact, restitution, with the bounce dying out over the trip); each impact
 * jolts the pose (`wobble`), and the jolt decays and is additionally blended
 * out by `1 - s`, so it can never shift the arrival.
 *
 * All of it is a pure `step(dt)` with no clock and no DOM: the caller owns the
 * time.
 */

import {
  qAxisAngle,
  qIdentity,
  qMul,
  qNormalize,
  qSlerp,
  vNormalize,
  vScale,
} from './geometry';

/** The table. Nothing exists below it. */
export const FLOOR_Y = -0.72;
const GRAVITY = 15;
/** How much momentum an impact gives back. Stone on wood, not rubber. */
const RESTITUTION = 0.62;
/** Below this, an impact is not a hit but resting contact. */
const BOUNCE_FLOOR = 0.3;
/** How fast a jolt runs back out of the pose. */
const WOBBLE_DECAY = 7;
/** From when the body rocks over its own edge, and how often. */
const ROCK_FROM = 0.72;
const ROCK_TURNS = 3;
/** From which share of the trip it counts as arrived. For the settle signal. */
const LANDED_AT = 0.95;

/** The stage when nobody has measured it. The renderer passes real extents. */
export const STAGE_X = 3.25;
export const STAGE_Z = 2.1;

export function randomAxis(rng) {
  for (let tries = 0; tries < 12; tries++) {
    const v = [rng() * 2 - 1, rng() * 2 - 1, rng() * 2 - 1];
    const l = v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
    if (l > 0.04 && l <= 1) return vNormalize(v);
  }
  return [0, 1, 0];
}

/**
 * How much bounce is left at time `s`. The first half bounces fully. That is
 * where the die should tumble. After that the rebound falls linearly to zero so
 * the ending happens **on the table** and not in the air.
 */
function damp(s) {
  return Math.max(0, 1 - Math.max(0, (s - 0.55) / 0.35));
}

/** Where a die comes to rest: on the table, not in it. */
export function restHeight(radius) {
  return FLOOR_Y + radius * 0.78;
}

/**
 * **Fold the unfolded table back up.**
 *
 * A triangle wave of amplitude `wall` with period `4·wall`. Exactly the
 * distance between the two walls and back.
 */
function fold(u, wall) {
  const s = Math.sin((Math.PI * u) / (2 * wall));
  return ((2 * wall) / Math.PI) * Math.asin(s < -1 ? -1 : s > 1 ? 1 : s);
}

/** Which unfolded tile `u` lies in. A change of tile is a wall hit. */
function cellOf(u, wall) {
  return Math.floor((u + wall) / (2 * wall));
}

/**
 * **Which of the two walls stands between two tiles: `+wall` or `-wall`.**
 *
 * Direction-free, because it asks about the *boundary*, not the tile: between
 * tiles `k` and `k+1` the boundary is at `u = (2k+1)·wall`, where `fold` is
 * exactly `(-1)^k · wall`. So the smaller of the two tile numbers decides, no
 * matter which way the die was travelling.
 */
function wallSide(prev, next) {
  return Math.min(prev, next) % 2 === 0 ? 1 : -1;
}

/**
 * **The distance along one axis that comes home after exactly `hits` walls.**
 *
 * After `hits` walls the die sits in tile `hits`, where `fold` is a straight
 * line; the point at which that line meets `home` again can be computed rather
 * than searched for.
 */
function spanFor(home, wall, hits, dir) {
  const straight = dir * 2 * hits * wall;
  return hits % 2 === 1 ? straight - 2 * home : straight;
}

/**
 * **How many wall hits each axis gets.**
 *
 * Not every pair works: if both axes hit in lockstep the die runs into a corner
 * and comes **straight back the way it came**. A line instead of a billiard.
 * Two such series never coincide exactly when the factor two occurs a different
 * number of times in the two hit counts. Hence a list, not a random number:
 * every pair in it has been checked.
 */
const WALL_PAIRS = [
  [1, 2],
  [2, 1],
  [2, 3],
  [3, 2],
  [1, 4],
  [4, 1],
  [3, 4],
  [4, 3],
];

/**
 * A die on its slot. It **lies** there. Visible and still. Until `beginRoll`
 * flicks it. That is the wait before the throw, not the throw.
 */
export function makeDie(rng, home = [0, 0], radius = 0.92, stage = [STAGE_X, STAGE_Z]) {
  let q = qAxisAngle(randomAxis(rng), rng() * Math.PI * 2);
  q = qNormalize(qMul(qAxisAngle(randomAxis(rng), rng() * Math.PI), q));
  return {
    phase: 'rest',
    p: [home[0], restHeight(radius), home[1]],
    v: [0, 0, 0],
    q,
    w: [0, 0, 0],
    home,
    radius,
    stage,
    delay: 0,
    target: q,
    t: 0,
    restFor: 0,
    bounces: 0,
    wallHits: 0,
    impact: null,
    wobble: qIdentity(),
    tour: planTour(home, radius, stage, rng),
    cellX: 0,
    cellZ: 0,
  };
}

/** Two axes far enough apart that it reads as tumbling. */
function tumbleAxes(rng) {
  const a = randomAxis(rng);
  for (let tries = 0; tries < 8; tries++) {
    const b = randomAxis(rng);
    if (Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2]) < 0.8) return [a, b];
  }
  return [a, vNormalize([a[1] - a[2], a[2] - a[0], a[0] - a[1]])];
}

/** Roll up the trip: a hit pair, two directions, a tempo, a tumble. */
function planTour(home, radius, stage, rng) {
  const wallX = Math.max(radius * 0.5, stage[0] - radius);
  const wallZ = Math.max(radius * 0.5, stage[1] - radius);
  const [hitsX, hitsZ] = WALL_PAIRS[
    Math.min(WALL_PAIRS.length - 1, Math.floor(rng() * WALL_PAIRS.length))
  ];
  const [axisA, axisB] = tumbleAxes(rng);
  const tipAngle = rng() * Math.PI * 2;

  return {
    spanX: spanFor(home[0], wallX, hitsX, rng() < 0.5 ? 1 : -1),
    spanZ: spanFor(home[1], wallZ, hitsZ, rng() < 0.5 ? 1 : -1),
    wallX,
    wallZ,
    duration: 1.45 + rng() * 0.4,
    // The run-out of the path: 1 would be uniform, higher means flick and
    // friction. Much higher and the die covers its whole path in the first
    // third and crawls the rest.
    ease: 1.55 + rng() * 0.35,
    axisA,
    axisB,
    // Seven whole turns between them: enough for a smear, not a slideshow.
    turnA: (rng() < 0.5 ? 1 : -1) * (26 + rng() * 18),
    turnB: (rng() < 0.5 ? 1 : -1) * (16 + rng() * 14),
    // Flatter than the path's run-out: the die should spin **to the last
    // frame** rather than stop halfway and wait.
    spinEase: 1.8 + rng() * 0.4,
    // Rocking happens over an edge, so about a **horizontal** axis.
    rockAxis: vNormalize([Math.cos(tipAngle), 0.12, Math.sin(tipAngle)]),
    rockAmount: 0.13 + rng() * 0.09,
  };
}

/**
 * **The pose at time `s`**. Written down, not accumulated.
 *
 * Read it bottom-up: the target pose, then the back-rotation by
 * `back = -(1-s)^spinEase`. At `s = 1` `back` is zero, every factor above is
 * identity, and what remains is exactly the target pose. Without anything
 * having rotated it there.
 */
function poseAt(die, s) {
  const { axisA, axisB, turnA, turnB, spinEase, rockAxis, rockAmount } = die.tour;
  const back = -Math.pow(1 - s, spinEase);

  let q = die.target;
  q = qMul(qAxisAngle(axisB, turnB * back), q);
  q = qMul(qAxisAngle(axisA, turnA * back), q);

  // The rock: a damped see-saw whose envelope is zero at both ends.
  const u = (s - ROCK_FROM) / (1 - ROCK_FROM);
  if (u > 0 && u < 1) {
    const envelope = (1 - u) * (1 - u);
    q = qMul(qAxisAngle(rockAxis, rockAmount * Math.sin(ROCK_TURNS * Math.PI * u) * envelope), q);
  }

  // The jolts. `1 - s` is the straitjacket: whatever would be left at the end
  // is blended out, so no impact can shift the arrival.
  if (s < 1) q = qMul(qSlerp(qIdentity(), die.wobble, 1 - s), q);
  return qNormalize(q);
}

/** How fast the pose turned between two frames, as a vector. */
function angularVelocity(from, to, dt) {
  if (dt <= 0) return [0, 0, 0];
  const d = qMul(to, { w: from.w, x: -from.x, y: -from.y, z: -from.z });
  const sin = Math.hypot(d.x, d.y, d.z);
  if (sin < 1e-9) return [0, 0, 0];
  // Rotate away the sign of `w`: otherwise the axis flips every time the pose
  // passes half a turn and the smear flickers.
  const flip = d.w < 0 ? -1 : 1;
  const angle = 2 * Math.atan2(sin, Math.abs(d.w));
  return vScale([(d.x * flip) / sin, (d.y * flip) / sin, (d.z * flip) / sin], angle / dt);
}

/**
 * The flick. It rolls up path and tumble and lifts the body off the table. It
 * does not get the pose. From here on that lives in `poseAt`.
 */
/**
 * `tour` overrides fields of the planned tour (duration, turnA, turnB, ...)
 * for a throw that is not the full flick: a die tipped onto a face by hand
 * tumbles the same way, only shorter and on the spot.
 */
export function beginRoll(die, target, delay, rng, tour = null) {
  die.phase = 'throw';
  die.p = [die.home[0], restHeight(die.radius) + 0.02, die.home[1]];
  die.tour = { ...planTour(die.home, die.radius, die.stage, rng), ...tour };
  die.cellX = cellOf(die.home[0], die.tour.wallX);
  die.cellZ = cellOf(die.home[1], die.tour.wallZ);
  die.v = [0, 2.9 + rng() * 1.0, 0];
  die.target = target;
  die.delay = delay;
  die.restFor = 0;
  die.bounces = 0;
  die.wallHits = 0;
  die.impact = null;
  die.wobble = qIdentity();
  die.t = 0;
  die.q = poseAt(die, 0);
  // The spin of the first frame, so the smear does not lag a round behind.
  const probe = 1 / 240 / die.tour.duration;
  die.w = angularVelocity(die.q, poseAt(die, probe), probe * die.tour.duration);
}

/** A jolt knocks the pose out. It decays. And `poseAt` blends it to nothing. */
function jolt(die, amount, rng) {
  die.wobble = qNormalize(qMul(qAxisAngle(randomAxis(rng), amount), die.wobble));
}

/**
 * The wall hit: a jolt, a hop, a bang.
 *
 * The bang belongs **at the wall**, not where the die happens to be on the next
 * frame. The tile change is always found one step late. And at the **contact
 * point**, not the center: `wall` is where the *center* sits when the body
 * kisses the wall, a full radius inside.
 */
function hitWall(die, normal, wall, here, s, vy, rng) {
  die.wallHits += 1;
  const punch = Math.max(0, 1 - s);
  jolt(die, 0.12 + punch * 0.3, rng);
  const skin = wall + die.radius;
  const at = [
    normal[0] !== 0 ? -normal[0] * skin : here[0],
    here[1],
    normal[2] !== 0 ? -normal[2] * skin : here[2],
  ];
  die.impact = { kind: 'wall', strength: 0.35 + punch * 0.65, at, normal };
  // The cushion throws it up. But only while the trip still has juice.
  return s < 0.8 ? Math.max(vy, 1.0 * punch) : vy;
}

export function stepDie(die, dt, rng) {
  die.impact = null;

  if (die.phase === 'rest') {
    die.restFor += dt;
    return;
  }

  if (die.delay > 0) {
    // Before its own flick the die lies still on its slot.
    die.delay -= dt;
    return;
  }

  die.t += dt;
  const { spanX, spanZ, wallX, wallZ, duration, ease } = die.tour;
  const s = Math.min(1, die.t / duration);

  // **The path.** A straight line on the unfolded table whose speed runs out
  // with the trip; `fold` turns it into the zigzag between the walls.
  // `tau(1) = 1`. Hence the last point is the home slot, and because the slope
  // there is zero it arrives without speed.
  const tau = 1 - Math.pow(1 - s, ease);
  const ux = die.home[0] + spanX * tau;
  const uz = die.home[1] + spanZ * tau;
  const x = fold(ux, wallX);
  const z = fold(uz, wallZ);

  // **The height** stays ballistics: fall, hit, bounce shorter.
  let vy = die.v[1] - GRAVITY * dt;
  let y = die.p[1] + vy * dt;
  const floor = restHeight(die.radius);
  if (y <= floor && vy < 0) {
    // **Resting is not an impact.** Without this threshold every frame on the
    // table counted as its own hit.
    const blow = -vy;
    y = floor;
    vy = blow > BOUNCE_FLOOR ? blow * RESTITUTION * damp(s) : 0;
    if (vy < 0.14) vy = 0;
    if (blow > BOUNCE_FLOOR) {
      const hit = Math.min(1, blow / 4);
      jolt(die, hit * 0.22 * (1 - s), rng);
      die.bounces += 1;
      die.impact = { kind: 'floor', strength: hit, at: [x, floor, z], normal: [0, 1, 0] };
    }
  }

  // **The walls.** A tile change means a wall stood exactly in between. Which
  // one is told by the pair of old and new tile. The new one alone is not
  // enough, see `wallSide`.
  const nextX = cellOf(ux, wallX);
  const nextZ = cellOf(uz, wallZ);
  const here = [x, y, z];
  if (nextX !== die.cellX) {
    const side = wallSide(die.cellX, nextX);
    die.cellX = nextX;
    vy = hitWall(die, [-side, 0, 0], wallX, here, s, vy, rng);
  }
  if (nextZ !== die.cellZ) {
    const side = wallSide(die.cellZ, nextZ);
    die.cellZ = nextZ;
    vy = hitWall(die, [0, 0, -side], wallZ, here, s, vy, rng);
  }

  // Velocity from the actual path. The smear needs it even though the path
  // comes from the timetable.
  if (dt > 0) die.v = [(x - die.p[0]) / dt, vy, (z - die.p[2]) / dt];
  die.p = [x, y, z];

  // **The spin.** Read off rather than accumulated, and the rate measured from it.
  die.wobble = qSlerp(die.wobble, qIdentity(), Math.min(1, WOBBLE_DECAY * dt));
  const before = die.q;
  die.q = poseAt(die, s);
  die.w = angularVelocity(before, die.q, dt);

  // The throw has landed. Reported a hair before the clock runs out so a
  // caller's cue falls on the last visible motion.
  const threshold = duration * LANDED_AT;
  if (die.t >= threshold && die.t - dt < threshold) {
    die.impact = { kind: 'settle', strength: 1, at: [x, floor, z], normal: [0, 1, 0] };
  }

  // Trip over and back on the ground: nothing more happens. The die already
  // stands on its number, because it never rotated anywhere else.
  if (s >= 1 && vy === 0 && y <= floor + 1e-6) {
    die.phase = 'rest';
    die.q = die.target;
    die.p = [die.home[0], floor, die.home[1]];
    die.v = [0, 0, 0];
    die.w = [0, 0, 0];
    die.wobble = qIdentity();
    die.restFor = 0;
  }
}

/** Lie down immediately. For reduced motion. */
export function restImmediately(die, target) {
  die.phase = 'rest';
  die.q = target;
  die.target = target;
  die.p = [die.home[0], restHeight(die.radius), die.home[1]];
  die.v = [0, 0, 0];
  die.w = [0, 0, 0];
  die.wobble = qIdentity();
  die.delay = 0;
  die.restFor = 0;
  die.impact = null;
}
